import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  generateTransitionSvg,
  type TransitionKind,
  type ShapeKind,
  type FlatEdge,
  type PatternKind,
  type FuzzFilter,
} from '@/lib/transition-svg';

export const Route = createFileRoute('/test-svg')({
  component: TestSvgPage,
});

type Mode = 'transition' | 'shape';

const TRANSITION_KINDS: { kind: TransitionKind; label: string; glyph: string }[] = [
  { kind: 'wave', label: 'Wave', glyph: '∿' },
  { kind: 'curve', label: 'Curve', glyph: '⌣' },
  { kind: 'square', label: 'Square', glyph: '⊓' },
  { kind: 'peaks', label: 'Peaks', glyph: '∧' },
  { kind: 'zigzag', label: 'Zigzag', glyph: '⋀' },
  { kind: 'mountains', label: 'Mountains', glyph: '⛰' },
  { kind: 'scallops', label: 'Scallops', glyph: '⌒' },
  { kind: 'drips', label: 'Drips', glyph: '⣀' },
  { kind: 'torn', label: 'Torn', glyph: '⚡' },
  { kind: 'diagonal', label: 'Diagonal', glyph: '╱' },
];

const SHAPE_KINDS: { kind: ShapeKind; label: string; glyph: string }[] = [
  { kind: 'circle', label: 'Circle', glyph: '●' },
  { kind: 'seal', label: 'Seal', glyph: '❀' },
  { kind: 'starburst', label: 'Starburst', glyph: '✦' },
  { kind: 'arch', label: 'Arch', glyph: '⌒' },
  { kind: 'pill', label: 'Pill', glyph: '▬' },
  { kind: 'blob', label: 'Blob', glyph: '✿' },
];

const PATTERNS: { kind: PatternKind | 'none'; label: string }[] = [
  { kind: 'none', label: 'None' },
  { kind: 'chevron-up', label: 'Chevron ↑' },
  { kind: 'chevron-down', label: 'Chevron ↓' },
  { kind: 'zigzag', label: 'Zigzag' },
  { kind: 'stripes-horizontal', label: 'Stripes —' },
  { kind: 'stripes-vertical', label: 'Stripes |' },
  { kind: 'stripes-diagonal', label: 'Stripes ╱' },
  { kind: 'dots', label: 'Dots' },
  { kind: 'crosshatch', label: 'Crosshatch' },
  { kind: 'waves-small', label: 'Waves' },
];

// Example image-backed boundaries to test pattern-fill against. The hero
// dough URL is the one that exposed the "flat-fill is always wrong over a
// photo" failure in the Baking Steel template — keep it as the default.
const IMAGE_PRESETS: { label: string; url: string; width: number; height: number }[] = [
  {
    label: 'Pizza dough hero (Baking Steel)',
    url: 'https://dev-media.nexmail.ai/org_3fVvK8LpPx/cl_LOspnC3mir/smart-hero-1778760961867-rvvj3n.png',
    width: 600,
    height: 520,
  },
  {
    label: 'Unsplash beach (open)',
    url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&q=80',
    width: 600,
    height: 400,
  },
];

function TestSvgPage() {
  const [mode, setMode] = useState<Mode>('transition');

  // Transition state
  const [tKind, setTKind] = useState<TransitionKind>('wave');
  const [flatEdge, setFlatEdge] = useState<FlatEdge>('top');
  const [tColor, setTColor] = useState('#0099ff');
  const [neighborColor, setNeighborColor] = useState('#f4f1ec');
  const [tComplexity, setTComplexity] = useState(50);
  const [tAmplitude, setTAmplitude] = useState(100);
  const [tHeight, setTHeight] = useState(40);
  const [tSeed, setTSeed] = useState(1);

  // Image-fill state — when imageFillOn, the owner section renders the
  // image and the wave pattern-fills with that same image's edge so the
  // photo visually continues through the silhouette.
  const [imageFillOn, setImageFillOn] = useState(false);
  const [imageUrl, setImageUrl] = useState(IMAGE_PRESETS[0].url);
  const [imageW, setImageW] = useState(IMAGE_PRESETS[0].width);
  const [imageH, setImageH] = useState(IMAGE_PRESETS[0].height);

  // Shape state
  const [sKind, setSKind] = useState<ShapeKind>('circle');
  const [sColor, setSColor] = useState('#6b3a4a');
  const [sBgColor, setSBgColor] = useState('#e87a82');
  const [sWidth, setSWidth] = useState(140);
  const [sHeight, setSHeight] = useState(140);
  const [sText, setSText] = useState('SWEET');
  const [sTextColor, setSTextColor] = useState('#ffffff');
  const [sFontSize, setSFontSize] = useState(20);
  const [sArchCurvature, setSArchCurvature] = useState(70);
  const [sComplexity, setSComplexity] = useState(50);
  const [sTeeth, setSTeeth] = useState(24);
  const [sToothDepth, setSToothDepth] = useState(10);
  const [sSeed, setSSeed] = useState(1);
  const [sPattern, setSPattern] = useState<PatternKind | 'none'>('none');
  const [sPatternColor, setSPatternColor] = useState('#ffd23f');
  const [sPatternScale, setSPatternScale] = useState(100);

  // Fuzz filter — shared across both modes. Applies turbulence
  // displacement to any silhouette for a fibrous/torn-paper edge.
  const [fuzzOn, setFuzzOn] = useState(false);
  const [fuzzFreq, setFuzzFreq] = useState(0.9);
  const [fuzzScale, setFuzzScale] = useState(12);
  const [fuzzOctaves, setFuzzOctaves] = useState(2);

  const [copied, setCopied] = useState(false);

  const fuzz: FuzzFilter | undefined = fuzzOn
    ? { frequency: fuzzFreq, scale: fuzzScale, octaves: fuzzOctaves }
    : undefined;

  const { svg } = useMemo(() => {
    if (mode === 'transition') {
      // Auto-flip mode (imageFillOn): the resolver swaps fill to the
      // neighbor's color and flips flatEdge so the wave bites INTO the
      // photo from the neighbor side. Mirror that here so the preview
      // shows what the resolver actually produces in production.
      const effectiveColor = imageFillOn ? neighborColor : tColor;
      const effectiveFlatEdge: FlatEdge = imageFillOn
        ? flatEdge === 'top'
          ? 'bottom'
          : 'top'
        : flatEdge;
      return generateTransitionSvg({
        kind: tKind,
        color: effectiveColor,
        flatEdge: effectiveFlatEdge,
        complexity: tComplexity,
        amplitude: tAmplitude,
        seed: tSeed,
        height: tHeight,
        width: 600,
        fuzz,
      });
    }
    return generateTransitionSvg({
      kind: sKind,
      color: sColor,
      width: sWidth,
      height: sHeight,
      complexity: sComplexity,
      seed: sSeed,
      text: sText || undefined,
      textColor: sTextColor,
      fontSize: sFontSize,
      archCurvature: sArchCurvature / 100,
      teeth: sTeeth,
      toothDepth: sToothDepth / 100,
      pattern:
        sPattern !== 'none'
          ? {
              kind: sPattern,
              color: sPatternColor,
              scale: sPatternScale / 100,
            }
          : undefined,
      fuzz,
    });
  }, [
    mode,
    tKind,
    flatEdge,
    tColor,
    tComplexity,
    tAmplitude,
    tHeight,
    tSeed,
    imageFillOn,
    imageUrl,
    imageW,
    imageH,
    sKind,
    sColor,
    sWidth,
    sHeight,
    sComplexity,
    sSeed,
    sText,
    sTextColor,
    sFontSize,
    sArchCurvature,
    sTeeth,
    sToothDepth,
    sPattern,
    sPatternColor,
    sPatternScale,
    fuzz,
  ]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(svg);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* swallow */
    }
  };

  const reseed = () => {
    const next = Math.floor(Math.random() * 1_000_000);
    if (mode === 'transition') setTSeed(next);
    else setSSeed(next);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f6f7f9',
        py: 5,
        px: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
        <Stack spacing={0.5} alignItems="center" sx={{ mb: 3 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}
          >
            Make some waves
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Deterministic SVG primitives for email design. Agent picks intent,
            this picks coordinates.
          </Typography>
        </Stack>

        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v as Mode)}
          centered
          sx={{ mb: 2 }}
        >
          <Tab label="Transitions" value="transition" />
          <Tab label="Shapes" value="shape" />
        </Tabs>

        {mode === 'transition' ? (
          <TransitionControls
            kind={tKind}
            onKindChange={setTKind}
            flatEdge={flatEdge}
            onFlatEdgeChange={setFlatEdge}
            color={tColor}
            onColorChange={setTColor}
            neighborColor={neighborColor}
            onNeighborColorChange={setNeighborColor}
            complexity={tComplexity}
            onComplexityChange={setTComplexity}
            amplitude={tAmplitude}
            onAmplitudeChange={setTAmplitude}
            height={tHeight}
            onHeightChange={setTHeight}
            imageFillOn={imageFillOn}
            onImageFillOnChange={setImageFillOn}
            imageUrl={imageUrl}
            onImageUrlChange={setImageUrl}
            imageW={imageW}
            onImageWChange={setImageW}
            imageH={imageH}
            onImageHChange={setImageH}
            onImagePreset={(p) => {
              setImageUrl(p.url);
              setImageW(p.width);
              setImageH(p.height);
            }}
            onRandomize={reseed}
          />
        ) : (
          <ShapeControls
            kind={sKind}
            onKindChange={setSKind}
            color={sColor}
            onColorChange={setSColor}
            bgColor={sBgColor}
            onBgColorChange={setSBgColor}
            width={sWidth}
            onWidthChange={setSWidth}
            height={sHeight}
            onHeightChange={setSHeight}
            complexity={sComplexity}
            onComplexityChange={setSComplexity}
            text={sText}
            onTextChange={setSText}
            textColor={sTextColor}
            onTextColorChange={setSTextColor}
            fontSize={sFontSize}
            onFontSizeChange={setSFontSize}
            archCurvature={sArchCurvature}
            onArchCurvatureChange={setSArchCurvature}
            teeth={sTeeth}
            onTeethChange={setSTeeth}
            toothDepth={sToothDepth}
            onToothDepthChange={setSToothDepth}
            pattern={sPattern}
            onPatternChange={setSPattern}
            patternColor={sPatternColor}
            onPatternColorChange={setSPatternColor}
            patternScale={sPatternScale}
            onPatternScaleChange={setSPatternScale}
            onRandomize={reseed}
          />
        )}

        {/* ─── Fuzz filter (shared) ─── */}
        <Box sx={{ mt: 2 }}>
          <ControlPanel>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <ControlGroup label="Fuzz filter">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={fuzzOn ? 'on' : 'off'}
                  onChange={(_, v) => v && setFuzzOn(v === 'on')}
                  sx={toggleGroupSx}
                >
                  <ToggleButton value="off">Off</ToggleButton>
                  <ToggleButton value="on">On</ToggleButton>
                </ToggleButtonGroup>
              </ControlGroup>
              {fuzzOn && (
                <>
                  <ControlGroup label="Frequency">
                    <Box sx={{ width: 160, pt: 0.5 }}>
                      <Slider
                        size="small"
                        value={fuzzFreq}
                        onChange={(_, v) => setFuzzFreq(v as number)}
                        min={0.05}
                        max={2}
                        step={0.05}
                      />
                    </Box>
                  </ControlGroup>
                  <ControlGroup label="Scale (px)">
                    <Box sx={{ width: 160, pt: 0.5 }}>
                      <Slider
                        size="small"
                        value={fuzzScale}
                        onChange={(_, v) => setFuzzScale(v as number)}
                        min={0}
                        max={60}
                      />
                    </Box>
                  </ControlGroup>
                  <ControlGroup label="Octaves">
                    <Box sx={{ width: 120, pt: 0.5 }}>
                      <Slider
                        size="small"
                        value={fuzzOctaves}
                        onChange={(_, v) => setFuzzOctaves(v as number)}
                        min={1}
                        max={4}
                        step={1}
                      />
                    </Box>
                  </ControlGroup>
                </>
              )}
            </Stack>
          </ControlPanel>
        </Box>

        {/* ─── Preview ─── */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow:
              '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
            mt: 3,
            mb: 3,
          }}
        >
          <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #eef0f3' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Preview
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
              bgcolor: '#f6f7f9',
            }}
          >
            {mode === 'transition' ? (
              <TransitionPreview
                svg={svg}
                color={tColor}
                neighborColor={neighborColor}
                flatEdge={flatEdge}
                stripHeight={tHeight}
                imageFillOn={imageFillOn}
                imageUrl={imageUrl}
                imageW={imageW}
                imageH={imageH}
              />
            ) : (
              <ShapePreview svg={svg} bgColor={sBgColor} />
            )}
          </Box>
        </Box>

        {/* ─── Output ─── */}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            p: 3,
            boxShadow:
              '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Rendered SVG
            </Typography>
            <Button
              size="small"
              onClick={copy}
              sx={{ textTransform: 'none' }}
            >
              {copied ? 'Copied' : 'Copy SVG'}
            </Button>
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: '#0f172a',
              color: '#e2e8f0',
              borderRadius: 2,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 11,
              lineHeight: 1.6,
              overflow: 'auto',
              maxHeight: 260,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {svg}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Transition controls ────────────────────────────────────────────────────

function TransitionControls(props: {
  kind: TransitionKind;
  onKindChange: (k: TransitionKind) => void;
  flatEdge: FlatEdge;
  onFlatEdgeChange: (e: FlatEdge) => void;
  color: string;
  onColorChange: (c: string) => void;
  neighborColor: string;
  onNeighborColorChange: (c: string) => void;
  complexity: number;
  onComplexityChange: (n: number) => void;
  amplitude: number;
  onAmplitudeChange: (n: number) => void;
  height: number;
  onHeightChange: (n: number) => void;
  imageFillOn: boolean;
  onImageFillOnChange: (v: boolean) => void;
  imageUrl: string;
  onImageUrlChange: (s: string) => void;
  imageW: number;
  onImageWChange: (n: number) => void;
  imageH: number;
  onImageHChange: (n: number) => void;
  onImagePreset: (p: { url: string; width: number; height: number }) => void;
  onRandomize: () => void;
}) {
  return (
    <ControlPanel>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        divider={<VerticalDivider />}
      >
        <ControlGroup label="Shape">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={props.kind}
            onChange={(_, v) => v && props.onKindChange(v as TransitionKind)}
            sx={toggleGroupSx}
          >
            {TRANSITION_KINDS.map((k) => (
              <ToggleButton key={k.kind} value={k.kind} title={k.label}>
                {k.glyph}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ControlGroup>

        <ControlGroup label="Flat edge">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={props.flatEdge}
            onChange={(_, v) => v && props.onFlatEdgeChange(v as FlatEdge)}
            sx={toggleGroupSx}
          >
            <ToggleButton value="top">Top ▔</ToggleButton>
            <ToggleButton value="bottom">▁ Bottom</ToggleButton>
          </ToggleButtonGroup>
        </ControlGroup>

        <ControlGroup label="Owner color">
          <ColorInput value={props.color} onChange={props.onColorChange} />
        </ControlGroup>

        <ControlGroup label="Neighbor color">
          <ColorInput
            value={props.neighborColor}
            onChange={props.onNeighborColorChange}
          />
        </ControlGroup>

        <RandomizeBtn onClick={props.onRandomize} />
      </Stack>
      <SliderRow
        items={[
          {
            label: 'Complexity',
            value: props.complexity,
            onChange: props.onComplexityChange,
            min: 0,
            max: 100,
          },
          {
            label: 'Amplitude',
            value: props.amplitude,
            onChange: props.onAmplitudeChange,
            min: 0,
            max: 100,
          },
          {
            label: 'Height (px)',
            value: props.height,
            onChange: props.onHeightChange,
            min: 10,
            max: 120,
          },
        ]}
      />

      {/* Image-fill panel — exercises v3 pattern-fill against a real photo. */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef0f3' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <ControlGroup label="Image-backed edge">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={props.imageFillOn ? 'on' : 'off'}
              onChange={(_, v) => v && props.onImageFillOnChange(v === 'on')}
              sx={toggleGroupSx}
            >
              <ToggleButton value="off">Off</ToggleButton>
              <ToggleButton value="on">On</ToggleButton>
            </ToggleButtonGroup>
          </ControlGroup>
          {props.imageFillOn && (
            <>
              <ControlGroup label="Preset">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={props.imageUrl}
                  onChange={(_, v) => {
                    if (!v) return;
                    const preset = IMAGE_PRESETS.find((p) => p.url === v);
                    if (preset) props.onImagePreset(preset);
                  }}
                  sx={{
                    ...toggleGroupSx,
                    '& .MuiToggleButton-root': {
                      ...toggleGroupSx['& .MuiToggleButton-root'],
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      textTransform: 'none',
                      px: 1,
                    },
                  }}
                >
                  {IMAGE_PRESETS.map((p) => (
                    <ToggleButton key={p.url} value={p.url}>
                      {p.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </ControlGroup>
              <ControlGroup label="URL">
                <TextField
                  size="small"
                  value={props.imageUrl}
                  onChange={(e) => props.onImageUrlChange(e.target.value)}
                  sx={{ width: 320 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }}
                />
              </ControlGroup>
              <ControlGroup label="W">
                <TextField
                  size="small"
                  type="number"
                  value={props.imageW}
                  onChange={(e) =>
                    props.onImageWChange(Number.parseInt(e.target.value, 10) || 0)
                  }
                  sx={{ width: 80 }}
                />
              </ControlGroup>
              <ControlGroup label="H">
                <TextField
                  size="small"
                  type="number"
                  value={props.imageH}
                  onChange={(e) =>
                    props.onImageHChange(Number.parseInt(e.target.value, 10) || 0)
                  }
                  sx={{ width: 80 }}
                />
              </ControlGroup>
            </>
          )}
        </Stack>
      </Box>
    </ControlPanel>
  );
}

// ─── Shape controls ─────────────────────────────────────────────────────────

function ShapeControls(props: {
  kind: ShapeKind;
  onKindChange: (k: ShapeKind) => void;
  color: string;
  onColorChange: (c: string) => void;
  bgColor: string;
  onBgColorChange: (c: string) => void;
  width: number;
  onWidthChange: (n: number) => void;
  height: number;
  onHeightChange: (n: number) => void;
  complexity: number;
  onComplexityChange: (n: number) => void;
  text: string;
  onTextChange: (s: string) => void;
  textColor: string;
  onTextColorChange: (c: string) => void;
  fontSize: number;
  onFontSizeChange: (n: number) => void;
  archCurvature: number;
  onArchCurvatureChange: (n: number) => void;
  teeth: number;
  onTeethChange: (n: number) => void;
  toothDepth: number;
  onToothDepthChange: (n: number) => void;
  pattern: PatternKind | 'none';
  onPatternChange: (p: PatternKind | 'none') => void;
  patternColor: string;
  onPatternColorChange: (c: string) => void;
  patternScale: number;
  onPatternScaleChange: (n: number) => void;
  onRandomize: () => void;
}) {
  return (
    <ControlPanel>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        divider={<VerticalDivider />}
      >
        <ControlGroup label="Shape">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={props.kind}
            onChange={(_, v) => v && props.onKindChange(v as ShapeKind)}
            sx={toggleGroupSx}
          >
            {SHAPE_KINDS.map((k) => (
              <ToggleButton key={k.kind} value={k.kind} title={k.label}>
                {k.glyph}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ControlGroup>

        <ControlGroup label="Fill color">
          <ColorInput value={props.color} onChange={props.onColorChange} />
        </ControlGroup>

        <ControlGroup label="Preview bg">
          <ColorInput
            value={props.bgColor}
            onChange={props.onBgColorChange}
          />
        </ControlGroup>

        <RandomizeBtn onClick={props.onRandomize} />
      </Stack>

      <SliderRow
        items={[
          {
            label: 'Width (px)',
            value: props.width,
            onChange: props.onWidthChange,
            min: 40,
            max: 600,
          },
          {
            label: 'Height (px)',
            value: props.height,
            onChange: props.onHeightChange,
            min: 40,
            max: 600,
          },
          ...(props.kind === 'arch'
            ? [
                {
                  label: 'Arch curvature',
                  value: props.archCurvature,
                  onChange: props.onArchCurvatureChange,
                  min: 0,
                  max: 100,
                },
              ]
            : []),
          ...(props.kind === 'blob'
            ? [
                {
                  label: 'Complexity',
                  value: props.complexity,
                  onChange: props.onComplexityChange,
                  min: 0,
                  max: 100,
                },
              ]
            : []),
          ...(props.kind === 'seal' || props.kind === 'starburst'
            ? [
                {
                  label: props.kind === 'seal' ? 'Teeth' : 'Points',
                  value: props.teeth,
                  onChange: props.onTeethChange,
                  min: 6,
                  max: props.kind === 'seal' ? 48 : 32,
                },
                {
                  label: 'Depth',
                  value: props.toothDepth,
                  onChange: props.onToothDepthChange,
                  min: props.kind === 'seal' ? 2 : 10,
                  max: props.kind === 'seal' ? 30 : 60,
                },
              ]
            : []),
        ]}
      />

      {/* Pattern controls */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef0f3' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <ControlGroup label="Pattern fill">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={props.pattern}
              onChange={(_, v) =>
                v && props.onPatternChange(v as PatternKind | 'none')
              }
              sx={{
                ...toggleGroupSx,
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  ...toggleGroupSx['& .MuiToggleButton-root'],
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  textTransform: 'none',
                  px: 1,
                },
              }}
            >
              {PATTERNS.map((p) => (
                <ToggleButton key={p.kind} value={p.kind}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </ControlGroup>
          {props.pattern !== 'none' && (
            <>
              <ControlGroup label="Pattern color">
                <ColorInput
                  value={props.patternColor}
                  onChange={props.onPatternColorChange}
                />
              </ControlGroup>
              <ControlGroup label="Pattern scale">
                <Box sx={{ width: 160, pt: 0.5 }}>
                  <Slider
                    size="small"
                    value={props.patternScale}
                    onChange={(_, v) => props.onPatternScaleChange(v as number)}
                    min={30}
                    max={300}
                  />
                </Box>
              </ControlGroup>
            </>
          )}
        </Stack>
      </Box>

      {/* Text controls (badges) */}
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef0f3' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <ControlGroup label="Text (badge)">
            <TextField
              size="small"
              value={props.text}
              onChange={(e) => props.onTextChange(e.target.value)}
              placeholder="SWEET"
              sx={{ width: 160 }}
            />
          </ControlGroup>
          <ControlGroup label="Text color">
            <ColorInput
              value={props.textColor}
              onChange={props.onTextColorChange}
            />
          </ControlGroup>
          <ControlGroup label="Font size">
            <Box sx={{ width: 160, pt: 0.5 }}>
              <Slider
                size="small"
                value={props.fontSize}
                onChange={(_, v) => props.onFontSizeChange(v as number)}
                min={8}
                max={64}
              />
            </Box>
          </ControlGroup>
        </Stack>
      </Box>
    </ControlPanel>
  );
}

// ─── Previews ───────────────────────────────────────────────────────────────

function TransitionPreview(props: {
  svg: string;
  color: string;
  neighborColor: string;
  flatEdge: FlatEdge;
  stripHeight: number;
  imageFillOn: boolean;
  imageUrl: string;
  imageW: number;
  imageH: number;
}) {
  const ownerOnTop = props.flatEdge === 'top';
  // When the owner section is image-backed, render it with a real <img>
  // sized to its declared (imageW × ownerSectionHeight) so the wave below
  // it visually continues the photo's bottom edge. Section height matches
  // the image's intrinsic height so the cover-crop alignment is honest.
  const ownerSectionHeight = props.imageFillOn
    ? Math.min(props.imageH, 360)
    : 100;
  const renderOwner = (position: 'top' | 'bottom') => {
    // In auto-flip mode the strip overlays the photo's edge (no overflow);
    // the cream wave bites INTO the photo, photo shows through the empty
    // negative space. In solid mode the strip overflows past the section
    // edge into the neighbor's territory as before.
    const stripPositionStyle = props.imageFillOn
      ? { [position === 'top' ? 'bottom' : 'top']: 0 }
      : { [position === 'top' ? 'bottom' : 'top']: -props.stripHeight };
    return (
      <Box
        sx={{
          position: 'relative',
          width: 600,
          height: ownerSectionHeight,
          bgcolor: props.imageFillOn ? 'transparent' : props.color,
          overflow: 'visible',
          zIndex: 1,
        }}
      >
        {props.imageFillOn && (
          <Box
            component="img"
            src={props.imageUrl}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition:
                position === 'top' ? 'center bottom' : 'center top',
              display: 'block',
            }}
          />
        )}
        <Typography
          sx={[
            sectionLabelSx,
            position === 'bottom' && { top: 'auto', bottom: 16 },
            props.imageFillOn && {
              color: 'rgba(255,255,255,0.85)',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            },
          ]}
        >
          SECTION {position === 'top' ? 'A' : 'B'} (owner
          {props.imageFillOn ? ', image-backed' : ''})
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            ...stripPositionStyle,
            left: 0,
            right: 0,
            height: `${props.stripHeight}px`,
            lineHeight: 0,
            zIndex: 2,
          }}
          dangerouslySetInnerHTML={{
            __html: props.svg.replace(
              /<svg /,
              '<svg style="display:block; width:100%; height:100%;" ',
            ),
          }}
        />
      </Box>
    );
  };
  const renderNeighbor = (position: 'top' | 'bottom') => (
    <Box
      sx={{
        position: 'relative',
        width: 600,
        height: 100,
        bgcolor: props.neighborColor,
        overflow: 'visible',
        zIndex: 0,
      }}
    >
      <Typography
        sx={{
          ...sectionLabelSx,
          ...(position === 'top' ? {} : { top: 'auto', bottom: 16 }),
        }}
      >
        SECTION {position === 'top' ? 'A' : 'B'}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        width: 600,
        position: 'relative',
        boxShadow: '0 0 0 1px #e5e7eb',
      }}
    >
      {ownerOnTop ? renderOwner('top') : renderNeighbor('top')}
      {ownerOnTop ? renderNeighbor('bottom') : renderOwner('bottom')}
    </Box>
  );
}

function ShapePreview(props: { svg: string; bgColor: string }) {
  return (
    <Box
      sx={{
        width: 600,
        minHeight: 380,
        bgcolor: props.bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 1px #e5e7eb',
        p: 4,
      }}
    >
      <Box dangerouslySetInnerHTML={{ __html: props.svg }} />
    </Box>
  );
}

// ─── Small UI helpers ───────────────────────────────────────────────────────

function ControlPanel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

function ControlGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function VerticalDivider() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        width: '1px',
        bgcolor: '#eef0f3',
        alignSelf: 'stretch',
      }}
    />
  );
}

function RandomizeBtn({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip title="Randomize seed">
      <IconButton
        onClick={onClick}
        sx={{
          bgcolor: '#0099ff',
          color: '#fff',
          width: 44,
          height: 44,
          '&:hover': { bgcolor: '#0080dd' },
        }}
      >
        <Box component="span" sx={{ fontSize: 20, fontWeight: 700 }}>
          ⟳
        </Box>
      </IconButton>
    </Tooltip>
  );
}

function ColorInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (c: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width: 110 }}
        inputProps={{ style: { fontFamily: 'monospace' } }}
      />
      <Box
        component="input"
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          width: 36,
          height: 36,
          p: 0,
          border: '1px solid #e5e7eb',
          borderRadius: 1.5,
          cursor: 'pointer',
          bgcolor: 'transparent',
        }}
      />
    </Stack>
  );
}

function SliderRow({
  items,
}: {
  items: {
    label: string;
    value: number;
    onChange: (n: number) => void;
    min: number;
    max: number;
  }[];
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={3}
      sx={{ mt: 2, px: 1 }}
    >
      {items.map((it) => (
        <Stack spacing={0.5} flex={1} key={it.label}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {it.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
              }}
            >
              {it.value}
            </Typography>
          </Stack>
          <Slider
            size="small"
            value={it.value}
            onChange={(_, v) => it.onChange(v as number)}
            min={it.min}
            max={it.max}
          />
        </Stack>
      ))}
    </Stack>
  );
}

const toggleGroupSx = {
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.75,
    border: '1px solid #e5e7eb',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 18,
    color: '#374151',
    '&.Mui-selected': {
      bgcolor: '#0099ff',
      color: '#fff',
      '&:hover': { bgcolor: '#0080dd' },
    },
  },
};

const sectionLabelSx = {
  position: 'absolute' as const,
  top: 16,
  left: 16,
  fontFamily: 'monospace',
  fontSize: 11,
  color: 'rgba(0,0,0,0.45)',
  letterSpacing: '0.1em',
};
